/**
 * ListenerStatusWidget — Read-Only Anzeige des IMAP-Listener-Status.
 *
 * Mount: /settings-Seite, oberhalb der Template-Tabs.
 * Polling: 30s via useListenerStatus-Hook.
 * CI-Farben: gruen=prod, gelb=dry-run, grau=inactive. NIE weisse Schrift auf Gelb.
 *
 * Phase 5 Plan 02 — ADMIN-07 als Status-Anzeige (env-Var-Mechanik unveraendert, D-01).
 */
import { useListenerStatus } from '../../hooks/useListenerStatus';
import type { ListenerStatus } from '../../types';

function formatRelative(iso: string | null): string {
  if (!iso) return 'noch nie';
  const t = new Date(iso).getTime();
  if (isNaN(t)) return 'unbekannt';
  const ageSec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (ageSec < 60) return `vor ${ageSec} s`;
  if (ageSec < 3600) return `vor ${Math.floor(ageSec / 60)} min`;
  if (ageSec < 86400) return `vor ${Math.floor(ageSec / 3600)} h`;
  return `vor ${Math.floor(ageSec / 86400)} Tagen`;
}

function modeBadge(status: ListenerStatus | null): {
  dotClass: string;
  text: string;
  textColor: string;
} {
  const mode = status?.mode ?? 'inactive';
  if (mode === 'prod') {
    return { dotClass: 'bg-green-500', text: 'Produktion aktiv', textColor: 'text-green-700' };
  }
  if (mode === 'dry-run') {
    // CI: gelber Punkt, NIEMALS weisse Schrift auf Gelb — textColor muss dunkel sein
    return {
      dotClass: 'bg-[#E9FF57]',
      text: 'Dry-Run aktiv (Staging)',
      textColor: 'text-[#1A1A1A]',
    };
  }
  return { dotClass: 'bg-[#DAD9D9]', text: 'Listener inaktiv', textColor: 'text-gray-600' };
}

export default function ListenerStatusWidget() {
  const { data, loading, error } = useListenerStatus(30_000);

  if (loading) {
    return (
      <div className="mb-6 p-4 border border-[#DAD9D9] rounded-lg bg-white">
        <h3 className="text-sm font-semibold text-[#1A1A1A] mb-2">Listener-Status</h3>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <span className="inline-block w-3 h-3 rounded-full bg-[#DAD9D9] animate-pulse" />
          <span>Status wird geladen ...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-6 p-4 border border-red-200 rounded-lg bg-red-50">
        <h3 className="text-sm font-semibold text-red-700 mb-2">Listener-Status</h3>
        <p className="text-sm text-red-700">{error}</p>
      </div>
    );
  }

  const badge = modeBadge(data);
  const opMode = data?.operatingMode ?? 'off';
  const opBadge =
    opMode === 'live'
      ? { text: '🚀 Live — Versand an alle', cls: 'bg-green-100 text-green-800' }
      : opMode === 'test'
        ? { text: `🧪 Test — nur Allowlist (${data?.allowlistCount ?? 0}), Timer ${data?.testTimerMinutes ?? '?'} Min`, cls: 'bg-[#E9FF57] text-[#1A1A1A]' }
        : { text: '⏸ Aus — kein Versand, kein Auto-Move', cls: 'bg-[#DAD9D9] text-[#1A1A1A]' };

  return (
    <div className="mb-6 p-4 border border-[#DAD9D9] rounded-lg bg-white">
      <h3 className="text-sm font-semibold text-[#1A1A1A] mb-2">Listener-Status</h3>
      <div className="flex flex-wrap items-center gap-3">
        <span className={`inline-block w-3 h-3 rounded-full ${badge.dotClass}`} aria-hidden />
        <span className={`text-sm font-medium ${badge.textColor}`}>{badge.text}</span>
        <span className="text-xs text-gray-500">
          Letzter Heartbeat: {formatRelative(data?.lastHeartbeatAt ?? null)}
        </span>
      </div>
      {/* Betriebsmodus — wird im Widget darunter umgeschaltet */}
      <div className="mt-2">
        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${opBadge.cls}`}>
          {opBadge.text}
        </span>
      </div>
    </div>
  );
}
